import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Loader2, X } from 'lucide-react';

// One app-wide PDF preview: `await previewPdf(blob, filename)` → true (Download) / false (Cancel).
let openFn = null;
export const previewPdf = (blob, filename) => (openFn ? new Promise((resolve) => openFn({ blob, filename, resolve })) : Promise.resolve(true));

const Pages = ({ blob }) => {
  const box = useRef(null);
  const [state, setState] = useState('loading');
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
        const doc = await pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise;
        const width = Math.min(box.current?.clientWidth || 700, 820);
        for (let i = 1; i <= doc.numPages && !dead; i += 1) {
          const page = await doc.getPage(i);
          const vp = page.getViewport({ scale: 1 });
          const scale = (width / vp.width) * (window.devicePixelRatio || 1);
          const v = page.getViewport({ scale });
          const c = document.createElement('canvas');
          c.width = v.width;
          c.height = v.height;
          c.style.width = '100%';
          c.className = 'mb-3 rounded bg-white shadow';
          c.dataset.testid = `pdf-preview-page-${i}`;
          await page.render({ canvasContext: c.getContext('2d'), viewport: v }).promise;
          if (!dead) box.current?.appendChild(c);
        }
        if (!dead) setState('ready');
      } catch (e) { if (!dead) setState('error'); }
    })();
    return () => { dead = true; };
  }, [blob]);
  return (
    <div ref={box} className="min-h-[200px]" data-testid="pdf-preview-pages">
      {state === 'loading' && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>}
      {state === 'error' && <p className="py-8 text-center text-sm text-zinc-400">Preview unavailable — you can still download it.</p>}
    </div>
  );
};

export const PdfPreviewHost = () => {
  const [job, setJob] = useState(null);
  useEffect(() => { openFn = setJob; return () => { openFn = null; }; }, []);
  if (!job) return null;
  const done = (ok) => { job.resolve(ok); setJob(null); };
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex flex-col bg-black/85" data-testid="pdf-preview-modal">
      <div className="flex items-center gap-2 border-b border-zinc-800 bg-zinc-950 px-3 py-2">
        <p className="min-w-0 flex-1 truncate text-sm font-bold text-white" data-testid="pdf-preview-title">Preview · {job.filename}</p>
        <button type="button" onClick={() => done(false)} className="flex items-center gap-1 rounded-md border border-zinc-700 px-3 py-1.5 text-xs font-bold text-zinc-200" data-testid="pdf-preview-cancel"><X className="h-4 w-4" />Cancel</button>
        <button type="button" onClick={() => done(true)} className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white" data-testid="pdf-preview-download"><Download className="h-4 w-4" />Download</button>
      </div>
      <div className="flex-1 overflow-y-auto p-3"><div className="mx-auto max-w-[820px]"><Pages blob={job.blob} /></div></div>
    </div>, document.body,
  );
};

export default PdfPreviewHost;
